import type { PastVisitView } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useState } from "react";
import { Link } from "react-router";
import { time12 } from "./AppointmentDialog";
import { BoulevardReceipt } from "./BoulevardReceipt";
import { SourceBadge } from "./SourceBadge";
import { Badge } from "./ui";

/** A visit from the old system (Boulevard) in the client's visit list, tagged: what was done, by whom, and its chart. Click for each charge. */
export function PastVisitRow({ v, chartId }: { v: PastVisitView; chartId?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="flex flex-col gap-1 border-b border-mist py-2.5 last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-40 text-sm tabular-nums">
          {new Date(`${v.date}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}
          {v.start ? ` · ${time12(`${v.date}T${v.start}`)}` : ""}
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold">
          {v.items.filter((i) => !i.addOn).map((i) => i.name).join(", ") || "Visit"}
          {v.items.some((i) => i.addOn) && <span className="font-normal text-ink-soft"> + {v.items.filter((i) => i.addOn).map((i) => i.name).join(", ")}</span>}
          {v.nurse && <span className="font-normal text-muted"> · {v.nurse}</span>}
        </span>
        <SourceBadge source={v.source} />
        {v.status && v.status !== "Final" && <Badge tone="wait">Not closed out ({v.status})</Badge>}
        <button type="button" aria-expanded={open} className="text-sm tabular-nums text-ink-soft hover:text-brand" title={v.receipt ? "Show the receipt" : "Show each service's net sale"} onClick={() => setOpen(!open)}>
          {formatCents(v.receipt ? v.receipt.totalCents : v.totalCents)} {open ? "▴" : "▾"}
        </button>
        {chartId && (
          <Link to={`/records/${chartId}`} className="text-sm font-semibold text-brand hover:text-brand-deep">
            Chart
          </Link>
        )}
      </div>
      {v.discount && <div className="pl-[10.75rem] text-xs text-muted">Discount: {v.discount}</div>}
      {open && v.receipt && <BoulevardReceipt r={v.receipt} className="ml-[10.75rem] max-w-md" />}
      {open && !v.receipt && (
        <ul className="ml-[10.75rem] flex max-w-md flex-col gap-0.5 text-xs text-ink-soft">
          {v.items.map((i, n) => (
            <li key={n} className="flex justify-between gap-3">
              <span className={i.addOn ? "pl-3" : ""}>
                {i.name}
                {i.category && <span className="text-muted"> · {i.category}</span>}
              </span>
              <span className="tabular-nums">{formatCents(i.amountCents)}</span>
            </li>
          ))}
          <li className="flex justify-between gap-3 border-t border-mist pt-0.5 font-semibold">
            <span>Services after discounts (before tip)</span>
            <span className="tabular-nums">{formatCents(v.totalCents)}</span>
          </li>
        </ul>
      )}
    </li>
  );
}
